'use client'

import { InfoCircle16 } from '@vinted/monochrome-icons'
import { Card, Cell, Dialog, Divider, Icon, List, Spacer, Text } from '@vinted/web-ui'
import { useEffect, useState } from 'react'

import { useFetch } from '@marketplace-web/core-api/api-client-util'
import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'
import { useBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useSession } from '@marketplace-web/shared/session-data'

import {
  getTermsAndConditions,
  getTermsAndConditionsContent,
  ModalInformationDto,
  TermsAndConditionsContentActionsDto,
  transformTermsAndConditionsContentResponse,
  transformTermsAndConditionsResponse,
} from '@marketplace-web/terms-and-conditions/terms-and-conditions-data'

import { InfoBanner } from '@marketplace-web/info-banner/info-banner-feature'

import TermsActions from './TermsActions'

const TermsAndConditions = () => {
  const breakpoints = useBreakpoint()
  const { isWebview } = useSession()
  const translate = useTranslate('terms_and_conditions')
  const a11yTranslation = useTranslate('common.a11y.actions')
  const [showInformationModal, setShowInformationModal] = useState(false)
  const [modalInformation, setModalInformation] = useState<ModalInformationDto>()
  const [showAcceptErrorModal, setShowAcceptErrorModal] = useState(false)
  const isSkipTnCUpdateBannerEnabled = useFeatureSwitch('skip_tnc_update_banner')

  const { fetch: fetchTermsAndConditions, transformedData: termsAndConditions } = useFetch(
    getTermsAndConditions,
    transformTermsAndConditionsResponse,
  )

  const { fetch: fetchTermsAndConditionsContent, transformedData: termsAndConditionsContent } =
    useFetch(getTermsAndConditionsContent, transformTermsAndConditionsContentResponse)

  useEffect(() => {
    const termsAndConditionsApiTrigger = async () => {
      await Promise.all([fetchTermsAndConditionsContent(), fetchTermsAndConditions()])
    }
    termsAndConditionsApiTrigger()
  }, [fetchTermsAndConditions, fetchTermsAndConditionsContent])

  const hasActions = !termsAndConditions?.canAgree || isWebview

  const renderActions = () => {
    if (hasActions) return null

    return <TermsActions showAcceptErrorModal={setShowAcceptErrorModal} />
  }

  const renderBottomActions = () => {
    if (hasActions) return null

    return (
      <>
        <Divider />
        <Spacer size="large" />
        {renderActions()}
      </>
    )
  }

  const renderUpdatesContent = () => {
    return (
      <>
        <Text text={termsAndConditionsContent?.updates?.title} type="heading" as="h2" />
        <Spacer size={breakpoints.desktops ? 'x-large' : 'medium'} />
        <Text as="span" text={termsAndConditionsContent?.updates?.text} theme="muted" html />
        <>
          <Spacer size="x-large" />
          {renderActions()}
        </>
      </>
    )
  }

  const renderUpdates = () => {
    if (
      !termsAndConditionsContent?.updates?.title ||
      !termsAndConditionsContent?.updates?.text ||
      isSkipTnCUpdateBannerEnabled
    )
      return null

    if (breakpoints.desktops) {
      return (
        <>
          <Spacer size="x2-large" />
          <Card styling="elevated">
            <div className="u-ui-padding-x2-large">{renderUpdatesContent()}</div>
          </Card>
          <Spacer size="x2-large" />
        </>
      )
    }

    return (
      <>
        <Spacer size="x-large" />
        {renderUpdatesContent()}
        <Spacer size="x2-large" />
      </>
    )
  }

  const handleInformationModalShow = information => {
    setShowInformationModal(true)
    setModalInformation(information)
  }

  const handleInformationModalHide = () => {
    setShowInformationModal(false)
  }

  const handleAcceptErrorModalHide = () => {
    setShowAcceptErrorModal(false)
  }

  const renderInformationModal = () => {
    return (
      <Dialog
        show={showInformationModal}
        defaultCallback={handleInformationModalHide}
        title={modalInformation?.title}
        body={modalInformation?.text}
        actions={[
          {
            text: translate('modal.actions.close'),
            callback: handleInformationModalHide,
            style: Dialog.ActionStyling.Filled,
          },
        ]}
      />
    )
  }

  const renderAcceptErrorModal = () => {
    return (
      <Dialog
        show={showAcceptErrorModal}
        defaultCallback={handleAcceptErrorModalHide}
        closeOnOverlay
        title={translate('error_modal.title')}
        body={translate('error_modal.description')}
        actions={[
          {
            text: translate('error_modal.try_again_action'),
            callback: handleAcceptErrorModalHide,
            style: Dialog.ActionStyling.Filled,
          },
        ]}
        testId="terms-accept-error-modal"
      />
    )
  }

  const renderAdditionalActions = () => {
    if (termsAndConditionsContent && !termsAndConditionsContent?.additionalActions) return null

    return (
      <div className="u-ui-margin-bottom-x2-large">
        <div className="u-ui-padding-vertical-medium">
          <Text as="span" text={termsAndConditionsContent?.additionalActions?.text} html />
        </div>
        <List size="tight">
          {termsAndConditionsContent?.additionalActions?.actions.map(
            ({ title, url, information }: TermsAndConditionsContentActionsDto, index: number) => (
              <List.Item key={index}>
                {information ? (
                  <Cell
                    styling="tight"
                    prefix={
                      <a href={url}>
                        <Text as="h2" underline text={title} type="title" theme="primary" />
                      </a>
                    }
                    body={
                      <button
                        type="button"
                        style={{ height: '16px' }}
                        className="u-block"
                        onClick={() => handleInformationModalShow(information)}
                        aria-label={`${title}, ${a11yTranslation('learn_more')}`}
                      >
                        <Icon name={InfoCircle16} color="primary-default" />
                      </button>
                    }
                  />
                ) : (
                  <Cell
                    styling="tight"
                    body={
                      <a href={url}>
                        <Text as="h2" underline text={title} type="title" theme="primary" />
                      </a>
                    }
                  />
                )}
                <Spacer />
              </List.Item>
            ),
          )}
        </List>
      </div>
    )
  }

  return (
    <Cell>
      <InfoBanner screen="terms_view" />
      {renderUpdates()}
      {renderAdditionalActions()}
      <Text as="h1" text={termsAndConditionsContent?.title} type="heading" />
      <Spacer size="x-large" />
      <div className="user-created-content">
        <Text as="span" text={termsAndConditionsContent?.text} theme="muted" width="parent" html />
      </div>
      <Spacer size="x2-large" />
      {renderAdditionalActions()}
      {renderBottomActions()}
      {renderInformationModal()}
      {renderAcceptErrorModal()}
    </Cell>
  )
}

export default TermsAndConditions
